import { useRef, useCallback } from 'react';
import { streamGlp1BridgeChat } from '../../../../api/glp1BridgeChat';
import { normalizeV2Citation } from '../utils/composeAnswer';
import { PHI_BLOCKED_ANSWER } from '../utils/constants';

export function useStreamChat({ setMessages, setLastScreeningContext }) {
  const streamAbortRef = useRef(null);

  // v2 streaming: receives engine events and updates the assistant message
  // in-place as text/citations/grounding arrive. Returns the controller so
  // the caller can cancel.
  const runStream = useCallback(
    ({ assistantId, requestBody, onScreenerComplete, onDone }) => {
      if (streamAbortRef.current) {
        try { streamAbortRef.current.abort(); } catch (e) { /* ignore */ }
      }
      const controller = new AbortController();
      streamAbortRef.current = controller;

      const updateAssistant = (updater) => {
        setMessages((current) =>
          current.map((m) => (m.id === assistantId ? { ...m, ...updater(m) } : m))
        );
      };

      const promise = streamGlp1BridgeChat({
        ...requestBody,
        signal: controller.signal,
        onEvent: (eventType, data) => {
          if (eventType === 'meta') {
            updateAssistant(() => ({
              request_id: data?.request_id,
              engine_meta: data,
              phase: 'opening',
            }));
          } else if (eventType === 'answer_stream_start') {
            updateAssistant(() => ({ phase: 'reading' }));
          } else if (eventType === 'text_delta') {
            const key = data?.key;
            const delta = data?.delta || '';
            if (!delta) return;
            updateAssistant((m) => {
              if (key === 'direct_answer') {
                return {
                  direct_answer: (m.direct_answer || '') + delta,
                  streaming: true,
                  phase: 'drafting',
                };
              }
              if (key === 'body_markdown') {
                return {
                  body_markdown: (m.body_markdown || '') + delta,
                  streaming: true,
                  phase: 'drafting',
                };
              }
              return {};
            });
          } else if (eventType === 'citations_enriched') {
            const normalized = (data?.citations || []).map(normalizeV2Citation);
            updateAssistant(() => ({ citations: normalized, phase: 'verifying' }));
          } else if (eventType === 'answer_ready') {
            updateAssistant(() => ({
              confidence: data?.confidence,
              suggests_screener: Boolean(data?.suggests_screener),
              needs_screener: Boolean(data?.suggests_screener),
              not_answered_reason: data?.not_answered_reason || null,
              grounding_state: data?.grounding_pending ? 'pending' : 'skipped',
              streaming: false,
            }));
          } else if (eventType === 'grounding') {
            const isGrounded = data?.is_grounded;
            updateAssistant(() => ({
              confidence: data?.final_confidence || undefined,
              grounding_state: isGrounded ? 'verified' : 'review',
              grounding: {
                is_grounded: isGrounded,
                unsupported_claims: data?.unsupported_claims || [],
                notes: data?.notes,
              },
              phase: 'done',
            }));
          } else if (eventType === 'screener_complete') {
            const reply = data || {};
            const screening = reply.screening;
            const answer = reply.answer || {};
            const screenerCitations = (reply.citations || answer.citations || []).map(normalizeV2Citation);
            updateAssistant(() => ({
              direct_answer: answer.direct_answer || '',
              body_markdown: answer.body_markdown || '',
              citations: screenerCitations,
              confidence: reply.confidence || answer.confidence,
              next_actions: reply.next_actions || [],
              screening,
              not_final_coverage_determination: reply.not_final_coverage_determination,
              request_id: reply.request_id,
              streaming: false,
            }));
            if (screening) setLastScreeningContext(screening);
            onScreenerComplete?.(screening);
          } else if (eventType === 'log') {
            updateAssistant(() => ({ chat_log_id: data?.chat_log_id }));
          } else if (eventType === 'phi_blocked') {
            const answer = data?.answer || {};
            updateAssistant(() => ({
              direct_answer: answer.direct_answer || PHI_BLOCKED_ANSWER,
              error: 'phi_not_allowed',
              citations: [],
              streaming: false,
              phase: 'error',
            }));
          } else if (eventType === 'error') {
            updateAssistant(() => ({
              answer: data?.detail || 'Unable to answer this question right now.',
              error: data?.error || 'stream_failed',
              streaming: false,
              phase: 'error',
            }));
          } else if (eventType === 'done') {
            updateAssistant(() => ({
              streaming: false,
              latency_ms: data?.latency_ms,
            }));
            onDone?.(data);
          }
        },
      });

      return { promise, controller };
    },
    [setMessages, setLastScreeningContext]
  );

  return { streamAbortRef, runStream };
}
